import React, { useEffect, useState } from 'react';
import { getLoginOptions, resetStaffPinApi, errText } from '../services/api';
import { T } from '../styles/ds';
import OtpVerify from './OtpVerify';

// PH-47 / ADR-010 — "Forgot PIN": one OTP token resets ONE named person's own PIN — an Admin's
// included since ADR-016 (PH-110), which retired the second-channel step an Admin used to need.
// PH-71 retired the pre-cutover "reset all three shared role PINs at once" shape this component
// used to also render (`LegacyRolePinReset`) — every shop is created already on per-person staff
// login now, so there is no more "not yet cut over" state for it to have applied to.

const card = (children) => (
  <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
    <div style={{ background: '#fff', borderRadius: 14, padding: 32, boxShadow: '0 2px 12px rgba(0,0,0,0.1)', width: '100%', maxWidth: 400 }}>
      {children}
    </div>
  </div>
);

// ── reset ONE named person's PIN ─────────────────────────────────────────────────────────────
function StaffPinReset({ config, onComplete, onCancel }) {
  const STEPS = { PICK: 'pick', OTP: 'otp', NEW_PIN: 'new_pin', DONE: 'done' };
  const [step, setStep] = useState(STEPS.PICK);
  const [options, setOptions] = useState(null);
  const [optionsError, setOptionsError] = useState('');
  const [staffId, setStaffId] = useState('');
  const [otpToken, setOtpToken] = useState('');
  const [newPin, setNewPin] = useState('');
  const [newPinConfirm, setNewPinConfirm] = useState('');
  const [pinError, setPinError] = useState('');
  const [apiError, setApiError] = useState('');
  const [loading, setLoading] = useState(false);

  const loadOptions = () => {
    setOptions(null); setOptionsError('');
    getLoginOptions()
      .then(res => setOptions(res.data?.people || []))
      .catch(err => setOptionsError(errText(err, 'सूची लोड नहीं हुई / Could not load the staff list')));
  };
  useEffect(loadOptions, []);

  const validatePin = () => {
    if (!newPin) return 'PIN ज़रूरी है / PIN is required';
    if (!/^\d{6,12}$/.test(newPin)) return '6–12 अंक का PIN / 6–12 digit PIN';
    if (newPin !== newPinConfirm) return 'PIN मेल नहीं खाते / PINs do not match';
    return '';
  };

  const submitNewPin = async () => {
    const err = validatePin();
    setPinError(err);
    if (err) return;
    setLoading(true); setApiError('');
    try {
      await resetStaffPinApi({ otp_token: otpToken, staff_id: Number(staffId), new_pin: newPin });
      setStep(STEPS.DONE);
      setTimeout(() => onComplete(), 1000);
    } catch (e) {
      setApiError(errText(e, 'PIN नहीं बदला — फिर से शुरू करें / PIN reset failed — please start over'));
    } finally {
      setLoading(false);
    }
  };

  const startOver = () => {
    setOtpToken(''); setNewPin(''); setNewPinConfirm(''); setPinError(''); setApiError('');
    setStep(STEPS.PICK);
  };

  if (step === STEPS.PICK) return card(
    <>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 32, marginBottom: 8 }}>🔐</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: T.ink }}>PIN भूल गए?</div>
        <div style={{ fontSize: 13, color: T.mute, marginTop: 4 }}>Forgot PIN — {config.shopName}</div>
      </div>
      {options === null && !optionsError && (
        <div style={{ fontSize: 13, color: T.sec, textAlign: 'center', padding: '16px 0' }}>लोड हो रहा है... / Loading…</div>
      )}
      {optionsError && (
        <div role="alert" style={{ textAlign: 'center', padding: '10px 0' }}>
          <div style={{ color: T.red.text, fontSize: 13, marginBottom: 10 }}>{optionsError}</div>
          <button type="button" onClick={loadOptions} style={{ padding: '9px 18px', background: T.ink, color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            फिर से कोशिश करें / Retry
          </button>
        </div>
      )}
      {options !== null && !optionsError && options.length === 0 && (
        <div style={{ textAlign: 'center', padding: '16px 0', fontSize: 13, color: T.sec, lineHeight: 1.6 }}>
          अभी किसी का PIN सेट नहीं है — मालिक से पूछें<br />
          <span style={{ color: T.mute, fontSize: 12 }}>Nobody has a PIN set yet — ask the owner</span>
        </div>
      )}
      {options !== null && !optionsError && options.length > 0 && (
        <>
          <label htmlFor="reset-name-select" style={{ display: 'block', fontSize: 11, fontWeight: 600, color: T.mute, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            नाम / Name
          </label>
          <select id="reset-name-select" value={staffId} onChange={e => setStaffId(e.target.value)}
            style={{ width: '100%', padding: '13px', border: `2px solid ${T.border}`, borderRadius: 8, fontSize: 15, marginBottom: 16, background: '#f8f9fa', boxSizing: 'border-box' }}>
            <option value="">— चुनें / Choose —</option>
            {options.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </>
      )}
      <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
        <button onClick={onCancel} style={{ flex: 1, padding: '11px', background: T.bg2, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', color: T.sec }}>
          रद्द करें / Cancel
        </button>
        <button onClick={() => staffId && setStep(STEPS.OTP)} disabled={!staffId}
          style={{ flex: 1, padding: '11px', background: T.ink, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: staffId ? 'pointer' : 'default', color: '#fff', opacity: staffId ? 1 : 0.5 }}>
          आगे बढ़ें →
        </button>
      </div>
    </>
  );

  if (step === STEPS.OTP) return card(
    <OtpVerify
      config={config}
      title="OTP से पुष्टि करें"
      subtitle="Confirm with an OTP"
      onVerified={(token) => { setOtpToken(token); setStep(STEPS.NEW_PIN); }}
      onCancel={() => setStep(STEPS.PICK)}
    />
  );

  if (step === STEPS.NEW_PIN) return card(
    <>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 18, fontWeight: 800, color: T.ink }}>नया PIN डालें</div>
        <div style={{ fontSize: 13, color: T.mute, marginTop: 2 }}>Set the new PIN</div>
      </div>
      <input type="password" inputMode="numeric" maxLength={12} value={newPin} aria-label="नया PIN / New PIN"
        onChange={e => { setNewPin(e.target.value); setPinError(''); }}
        placeholder="6–12 digit PIN"
        style={{ width: '100%', padding: '13px', border: `2px solid ${pinError ? T.red.text : T.border}`, borderRadius: 8, fontSize: 15, letterSpacing: 4, marginBottom: 10, boxSizing: 'border-box' }} />
      <input type="password" inputMode="numeric" maxLength={12} value={newPinConfirm} aria-label="PIN फिर से डालें / Confirm PIN"
        onChange={e => { setNewPinConfirm(e.target.value); setPinError(''); }}
        placeholder="Confirm PIN"
        style={{ width: '100%', padding: '13px', border: `2px solid ${pinError ? T.red.text : T.border}`, borderRadius: 8, fontSize: 15, letterSpacing: 4, marginBottom: 4, boxSizing: 'border-box' }} />
      {pinError && <div role="alert" style={{ color: T.red.text, fontSize: 12, marginBottom: 8 }}>{pinError}</div>}
      {apiError && <div role="alert" style={{ color: T.red.text, fontSize: 12, marginBottom: 8, textAlign: 'center' }}>{apiError}</div>}
      <button onClick={submitNewPin} disabled={loading}
        style={{ width: '100%', marginTop: 6, padding: '13px', background: T.green.text, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1 }}>
        {loading ? 'सेव हो रहा है...' : 'PIN सेव करें / Save PIN ✓'}
      </button>
      {/* PH-110 — a code that expired or was already used cannot be retried from here; this is
          the way back to the name list and a fresh code. */}
      <button type="button" onClick={startOver} disabled={loading}
        style={{ width: '100%', minHeight: 44, marginTop: 8, padding: '11px', background: T.bg2, border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: loading ? 'default' : 'pointer', color: T.sec }}>
        फिर से शुरू करें / Start over
      </button>
    </>
  );

  return card(
    <div style={{ textAlign: 'center', padding: '20px 0' }}>
      <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: T.green.text }}>PIN बदल गया!</div>
      <div style={{ fontSize: 13, color: T.mute, marginTop: 4 }}>PIN updated successfully</div>
    </div>
  );
}

function ResetStaffPin({ config, onComplete, onCancel }) {
  return <StaffPinReset config={config} onComplete={onComplete} onCancel={onCancel} />;
}

export default ResetStaffPin;
